import React, { useState } from "react";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import {
  DateFormatCell,
  AmountFormatCellEmptyZero,
} from "components/common/formatter";
import {
  cellWithBackGround,
  createRowRender,
  RefCell,
  CenterTextHeaderCell,
  ToolbarHeaderCell,
} from "./LedgerGridCells";
import styles from "./LedgerGrid.module.scss";
import { GridLoader } from "components/GridLoader";
import { exportData } from "components/Report/exportUtility";
import { ExportConfigs } from "components/Report/exportConfigs";
import PreviousTransactionsGrid from "./MatterPreviousTransactions";
import { getLedgerExportFileName } from "./ledgerExportFileName";
import { usePostingStyle } from "./usePostingStyle";

const LedgerTypeGrid = ({
  gridData,
  ledgerType,
  currencyCode,
  loading,
  handleActionRef,
  matterId,
  matterRef,
  setOpenReport,
  isClosed,
  dateClosed,
  closedByUserRef,
  prevTransTitle,
  showPreviousTransactions = false,
  exportTabLabel,
  fillAvailableHeight = false,
}) => {
  const [showPrevTrans, setShowPrevTrans] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { postingStyleMap } = usePostingStyle();

  const gridClass = fillAvailableHeight
    ? styles.grid_container_fill
    : styles.grid_container;

  const exportReport = async (isPreviousTransactions = false) => {
    try {
      setIsExporting(true);
      const exportOptions = isPreviousTransactions
        ? {
          ledgerPostingType: "Client",
          currencyCode,
          reportDocumentName: matterRef || "Ledger",
        }
        : {
          ledgerPostingType: ledgerType,
          reportDocumentName: matterRef || "Ledger",
        };
      await exportData({
        ...ExportConfigs.MATTER_LEDGER,
        requestBody: ExportConfigs.MATTER_LEDGER.getRequestBody(
          matterId,
          isPreviousTransactions,
          exportOptions,
        ),
        fileName: getLedgerExportFileName({
          tabLabel: exportTabLabel,
          matterRef,
        }),
        onStart: () => setIsExporting(true),
        onComplete: () => setIsExporting(false),
        onError: () => setIsExporting(false),
      });
    } catch (error) {
      setIsExporting(false);
    }
  };

  const disableReportExport =
    ledgerType === "Client" && Boolean(currencyCode);

  const toolbarHeaderCell = (props) =>
    ToolbarHeaderCell({
      ...props,
      matterId,
      isClosed,
      dateClosed,
      closedByUserRef,
      onPrint: () => {
        if (matterId) setOpenReport?.(true);
      },
      onShowPrevTrans: () => setShowPrevTrans(true),
      isExporting,
      exportReport,
      showPreviousTransactions,
      disablePrint: disableReportExport,
      disableExport: disableReportExport,
    });

  const baseColumns = [
    {
      field: "date",
      title: "Date",
      width: "100px",
      cell: DateFormatCell,
    },
    {
      field: "postingType",
      title: "Type",
      width: "100px",
    },
    {
      field: "reference",
      title: "Ref",
      width: "110px",
      cell: (props) => RefCell({ ...props }, handleActionRef),
    },
    {
      field: "details",
      title: "Details",
      width: "320px",
      className: "text-prewrap",
    },
  ];

  const salePurchaseColumns = [
    {
      field: "vatAmount",
      title: "VAT",
      width: "90px",
      headerCell: CenterTextHeaderCell,
      cell: AmountFormatCellEmptyZero,
    },
    {
      field: "grossAmount",
      title: "Debit",
      width: "95px",
      headerCell: CenterTextHeaderCell,
      cell: (e) => cellWithBackGround(e, "OFFICE", "Debit"),
    },
    {
      field: "grossAmount",
      title: "Credit",
      width: "95px",
      headerCell: CenterTextHeaderCell,
      cell: (e) => cellWithBackGround(e, "OFFICE", "Credit"),
    },
    {
      field: "balance",
      title: "Balance",
      width: "95px",
      headerCell: CenterTextHeaderCell,
      cell: (e) => cellWithBackGround(e, "OFFICE", "Amount"),
    },
  ];

  const clientColumns = [
    {
      field: "clientAmount",
      title: "Debit",
      width: "95px",
      headerCell: CenterTextHeaderCell,
      cell: (e) => cellWithBackGround(e, "CLIENT", "Debit"),
    },
    {
      field: "clientAmount",
      title: "Credit",
      width: "95px",
      headerCell: CenterTextHeaderCell,
      cell: (e) => cellWithBackGround(e, "CLIENT", "Credit"),
    },
    {
      field: "clientBalance",
      title: "Balance",
      width: "110px",
      headerCell: CenterTextHeaderCell,
      cell: (e) => cellWithBackGround(e, "CLIENT", "Amount"),
    },
  ];

  const isClientLedger = ledgerType === "Client";
  const dataColumns = isClientLedger
    ? [...baseColumns, ...clientColumns]
    : [...baseColumns, ...salePurchaseColumns];

  return (
    <div
      className={
        fillAvailableHeight
          ? "h-100 d-flex flex-column flex-grow-1"
          : "h-100"
      }
      style={fillAvailableHeight ? { minHeight: 0 } : undefined}
    >
      <Grid
        scroll
        loader={
          <div style={{ height: "500px" }}>
            <GridLoader />
          </div>
        }
        showLoader={loading}
        data={gridData}
        className={`h-100 ${gridClass} pb-1 mb-1 grid-fs13`}
        rowRender={createRowRender(postingStyleMap)}
      >
        <GridNoRecords>
          {loading ? (
            <span> </span>
          ) : (
            gridData && gridData?.length === 0 && "No records available"
          )}
        </GridNoRecords>
        <GridColumn headerCell={toolbarHeaderCell}>
          {dataColumns.map((col, colId) => (
            <GridColumn key={`col-${colId}`} {...col} />
          ))}
        </GridColumn>
      </Grid>

      {showPrevTrans && (
        <PreviousTransactionsGrid
          entity={{
            matterId,
            matterRef,
            ledgerPostingType: "Client",
            currencyCode,
          }}
          title={prevTransTitle || "Previous Transactions"}
          isOpen={showPrevTrans}
          onClose={() => setShowPrevTrans(false)}
          showDDA={false}
          onExport={exportReport}
          isExporting={isExporting}
          handleActionRef={handleActionRef}
        />
      )}
    </div>
  );
};

export default LedgerTypeGrid;
